import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid } from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import { socketConnection } from "../../services/socket";

import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import CampaignIcon from "@material-ui/icons/EventNote";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];
    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const idx = state.findIndex((u) => u.id === campaign.id);
        if (idx !== -1) state[idx] = campaign;
        else newCampaigns.push(campaign);
      });
    }
    return [...state, ...newCampaigns];
  }
  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const idx = state.findIndex((u) => u.id === campaign.id);
    if (idx !== -1) { state[idx] = campaign; return [...state]; }
    return [campaign, ...state];
  }
  if (action.type === "DELETE_CAMPAIGN") {
    const idx = state.findIndex((u) => u.id === action.payload);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const STATUS_CONFIG = {
  INATIVA:     { label: "Inativa",      color: "#9e9e9e", bg: "#f5f5f5" },
  PROGRAMADA:  { label: "Programada",   color: "#1565c0", bg: "#e3f2fd" },
  EM_ANDAMENTO:{ label: "Em Andamento", color: "#2e7d32", bg: "#e8f5e9" },
  CANCELADA:   { label: "Cancelada",    color: "#c62828", bg: "#ffebee" },
  FINALIZADA:  { label: "Finalizada",   color: "#6a1b9a", bg: "#f3e5f5" },
};

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#555", bg: "#eee" };
  return (
    <Chip
      size="small"
      label={cfg.label}
      style={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: "0.72rem",
        height: 22,
      }}
    />
  );
}

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  tableHead: {
    "& th": {
      fontWeight: 700,
      fontSize: "0.72rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.default,
      borderBottom: `2px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  filterChips: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    padding: theme.spacing(1, 0),
  },
  filterChip: {
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  dangerBtn: {
    color: theme.palette.error.main,
    "&:hover": { backgroundColor: theme.palette.error.light + "22" },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
    gap: theme.spacing(1),
  },
  // Cards para mobile
  campaignCard: {
    marginBottom: theme.spacing(1.5),
    borderRadius: 8,
  },
  cardMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  cardName: {
    fontWeight: 600,
    fontSize: "0.95rem",
  },
}));

const ALL_STATUSES = ["INATIVA", "PROGRAMADA", "EM_ANDAMENTO", "CANCELADA", "FINALIZADA"];

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const { datetimeToClient } = useDate();

  useEffect(() => {
    const hasAccepted = localStorage.getItem("campaignDisclaimerAccepted");
    if (!hasAccepted) setShowDisclaimer(true);
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => fetchCampaigns(), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber, statusFilter]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    socket.on(`company-${companyId}-campaign`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    });
    return () => socket.disconnect();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleStatusFilter = (status) => {
    setStatusFilter((prev) => (prev === status ? null : status));
  };

  const visibleCampaigns = statusFilter
    ? campaigns.filter((c) => c.status === statusFilter)
    : campaigns;

  const renderActions = (campaign) => (
    <>
      {campaign.status === "EM_ANDAMENTO" && (
        <Tooltip title="Pausar campanha">
          <IconButton size="small" onClick={() => cancelCampaign(campaign)}>
            <PauseCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      )}
      {campaign.status === "CANCELADA" && (
        <Tooltip title="Reiniciar campanha">
          <IconButton size="small" onClick={() => restartCampaign(campaign)}>
            <PlayCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Ver relatório">
        <IconButton size="small" onClick={() => history.push(`/campaign/${campaign.id}/report`)}>
          <DescriptionIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => handleEditCampaign(campaign)}>
          <EditIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton
          size="small"
          className={classes.dangerBtn}
          onClick={() => { setConfirmModalOpen(true); setDeletingCampaign(campaign); }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <>
      {/* Aviso legal */}
      <Dialog open={showDisclaimer} onClose={() => {}} aria-labelledby="disclaimer-dialog">
        <DialogTitle>{i18n.t("campaignsMessageAlert.disclaimer.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("campaignsMessageAlert.disclaimer.message")}
            <br /><br />
            <strong>{i18n.t("campaignsMessageAlert.disclaimer.responsibility")}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            autoFocus
            onClick={() => {
              localStorage.setItem("campaignDisclaimerAccepted", "true");
              setShowDisclaimer(false);
            }}
          >
            {i18n.t("campaignsMessageAlert.disclaimer.agree")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={deletingCampaign && `${i18n.t("campaignsMessageAlert.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaignsMessageAlert.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <CampaignModal
        resetPagination={() => { setPageNumber(1); fetchCampaigns(); }}
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        aria-labelledby="form-dialog-title"
        campaignId={selectedCampaign && selectedCampaign.id}
      />

      <MainContainer>
        <MainHeader>
          <Grid style={{ width: "99.6%" }} container>
            <Grid xs={12} sm={8} item>
              <Title>{i18n.t("campaigns.title")}</Title>
            </Grid>
            <Grid xs={12} sm={4} item>
              <Grid spacing={2} container>
                <Grid xs={6} sm={6} item>
                  <TextField
                    fullWidth
                    placeholder={i18n.t("campaigns.searchPlaceholder")}
                    type="search"
                    value={searchParam}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid xs={6} sm={6} item>
                  <Button fullWidth variant="contained" color="primary" onClick={handleOpenCampaignModal}>
                    {i18n.t("campaigns.buttons.add")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </MainHeader>

        {/* Filtros de status */}
        <Box className={classes.filterChips} px={1}>
          {ALL_STATUSES.map((s) => {
            const cfg = STATUS_CONFIG[s];
            const active = statusFilter === s;
            return (
              <Chip
                key={s}
                size="small"
                label={cfg.label}
                className={classes.filterChip}
                onClick={() => toggleStatusFilter(s)}
                style={{
                  backgroundColor: active ? cfg.color : cfg.bg,
                  color: active ? "#fff" : cfg.color,
                  border: `1px solid ${cfg.color}44`,
                  fontWeight: 600,
                  fontSize: "0.72rem",
                }}
              />
            );
          })}
        </Box>

        <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
          {/* Vista mobile — cards */}
          {isMobile ? (
            <>
              {visibleCampaigns.length === 0 && !loading && (
                <div className={classes.emptyState}>
                  <EventAvailableIcon style={{ fontSize: 48 }} />
                  <Typography variant="body2">Nenhuma campanha encontrada</Typography>
                </div>
              )}
              {visibleCampaigns.map((campaign) => (
                <Card key={campaign.id} variant="outlined" className={classes.campaignCard}>
                  <CardContent style={{ paddingBottom: 0 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography className={classes.cardName}>{campaign.name}</Typography>
                      <StatusChip status={campaign.status} />
                    </Box>
                    <Typography className={classes.cardMeta} style={{ marginTop: 4 }}>
                      {campaign.contactListId ? campaign.contactList.name : "Sem lista"} ·{" "}
                      {campaign.whatsappId ? campaign.whatsapp.name : "Sem conexão"}
                    </Typography>
                    {campaign.scheduledAt && (
                      <Typography className={classes.cardMeta}>
                        Agendado: {datetimeToClient(campaign.scheduledAt)}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions style={{ justifyContent: "flex-end", paddingTop: 0 }}>
                    {renderActions(campaign)}
                  </CardActions>
                </Card>
              ))}
              {loading && <Typography variant="body2" align="center">Carregando...</Typography>}
            </>
          ) : (
            /* Vista desktop — tabela */
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>{i18n.t("campaigns.table.name")}</TableCell>
                  <TableCell align="center">{i18n.t("campaigns.table.status")}</TableCell>
                  <TableCell>{i18n.t("campaigns.table.contactList")}</TableCell>
                  <TableCell>{i18n.t("campaigns.table.whatsapp")}</TableCell>
                  <TableCell align="center">{i18n.t("campaigns.table.scheduledAt")}</TableCell>
                  <TableCell align="center">{i18n.t("campaigns.table.confirmation")}</TableCell>
                  <TableCell align="center">{i18n.t("campaigns.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCampaigns.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <div className={classes.emptyState}>
                        <EventAvailableIcon style={{ fontSize: 48 }} />
                        <Typography variant="body2">Nenhuma campanha encontrada</Typography>
                        {statusFilter && (
                          <Button size="small" onClick={() => setStatusFilter(null)}>
                            Limpar filtro
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {visibleCampaigns.map((campaign) => (
                  <TableRow key={campaign.id} className={classes.tableRow}>
                    <TableCell>
                      <Typography style={{ fontWeight: 500, fontSize: "0.875rem" }}>
                        {campaign.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <StatusChip status={campaign.status} />
                    </TableCell>
                    <TableCell>
                      {campaign.contactListId ? campaign.contactList.name : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {campaign.whatsappId ? campaign.whatsapp.name : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.scheduledAt ? (
                        <Typography variant="body2">{datetimeToClient(campaign.scheduledAt)}</Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={campaign.confirmation ? "Sim" : "Não"}
                        style={{
                          backgroundColor: campaign.confirmation ? "#e8f5e9" : "#f5f5f5",
                          color: campaign.confirmation ? "#2e7d32" : "#757575",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {renderActions(campaign)}
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={7} />}
              </TableBody>
            </Table>
          )}
        </Paper>
      </MainContainer>
    </>
  );
};

export default Campaigns;
